'use client';

import { Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';

interface DeleteEntityDialogProps {
  open: boolean;
  title: string;
  entityLabel: string;
  entityName: string;
  warning?: string;
  onClose: () => void;
  onDelete: () => Promise<void>;
  testIdPrefix: string;
}

export const DeleteEntityDialog = ({
  open,
  title,
  entityLabel,
  entityName,
  warning,
  onClose,
  onDelete,
  testIdPrefix,
}: DeleteEntityDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setError(null);
    onClose();
  }, [onClose]);

  const handleDelete = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await onDelete();
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : `Failed to delete ${entityLabel}`
      );
    } finally {
      setIsLoading(false);
    }
  }, [onDelete, handleClose, entityLabel]);

  return (
    <MainDialog
      isOpen={open}
      isLoading={isLoading}
      header={{
        title,
        onClose: handleClose,
      }}
      footer={{
        primaryButton: {
          text: 'Delete',
          onClick: handleDelete,
          'data-testid': `${testIdPrefix}-btn`,
          disabled: isLoading,
        },
        secondaryButton: {
          text: 'Cancel',
          onClick: handleClose,
          'data-testid': `cancel-${testIdPrefix}-btn`,
        },
      }}
    >
      <Stack gap='16px' padding='0 40px'>
        <Typography>
          Are you sure you want to delete the {entityLabel}{' '}
          <strong>{entityName}</strong>?
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {warning ?? 'This action cannot be undone.'}
        </Typography>
        {error && (
          <Typography color='error' variant='body2'>
            {error}
          </Typography>
        )}
      </Stack>
    </MainDialog>
  );
};
